/**
 * The destination catalogue's WRITE calls (T651 M4) - create, rename,
 * deactivate.
 *
 * ⛔ WHY THIS IS ITS OWN MODULE AND NOT AN APPEND TO lib/actions.ts. That file
 * froze at T651 M2 so that three milestones filling three sibling screens could
 * run without editing the same declarations; the stated hazard is "two adjacent
 * appends to a shared client file". A per-area module produces ZERO appends to
 * any shared client file, which removes the hazard rather than managing it. This
 * is the same ruling lib/account-actions.ts records, applied identically.
 * ⚠️ Recorded honestly as a DEPARTURE from the shipped house convention:
 * screens/admin/Operators.tsx imports its write actions from actions.ts.
 *
 * ⛔ THE COPY IS ALWAYS THIS FILE'S OWN, NEVER THE SERVER'S SENTENCE (SEC-5),
 * matching actions.ts's adminFailure and account-actions.ts. The route wording is
 * terse and developer-shaped; these are the ones an operator reads.
 *
 * ⚠️ T699 NARROWED THAT SENTENCE AND IT IS RESTATED HERE RATHER THAN LEFT TO
 * READ AS FALSE. It used to say the copy comes from the STATUS and never from the
 * body. Since T699 the 400 arm also reads a machine-readable `code` OFF THE BODY
 * — but only to CHOOSE between sentences written here. No server prose is ever
 * rendered, so the rule the old line was protecting is intact; what changed is
 * that "status" is no longer the whole of what keys the map.
 * ⛔ This is NOT the body-rendering exception actions.ts's 403 arm makes. That
 * one puts the server's own words on screen; this one never does. See
 * shared/registry-error-schema.ts for why that distinction is the whole design.
 */
import { ApiError, postJson } from './api.js';
import { isRegistryErrorCode, type RegistryErrorCode } from '../../shared/registry-error-schema.js';

/**
 * The client's OWN sentence for each refusal code. ⛔ NOT the server's — the
 * route's wording is terse and developer-shaped, and SEC-5 keeps it off screen.
 * What crosses the wire is the CODE; this map is what Helen reads.
 */
const REGISTRY_ERROR_COPY: Readonly<Record<RegistryErrorCode, string>> = {
  field_too_long: 'That name is too long to save. Shorten it and try again.',
  // ⛔ DOES NOT SAY "INVISIBLE", and that is not fussiness: U+00B7 MIDDLE DOT is
  // in the refused set and is perfectly visible, as are the compatibility
  // ligatures a word processor produces. This sentence has to be true for every
  // member of the set while still giving her the one action that works.
  // ⭐ AND IT NAMES PASTING, because that is where these characters come from —
  // a hospital name copied off a letterhead or a PDF. Without that line she has
  // no reason to think the text she can see is not the text she has.
  field_charset:
    'That name has a character that cannot be saved. It usually comes from copying and pasting. '
    + 'Type the name in by hand and try again.',
  /**
   * ⛔ DEAD HERE BY CONSTRUCTION, AND PRESENT BECAUSE THE TYPE IS EXHAUSTIVE.
   * Both codes are raised by the ACCOUNT write routes only (T1719 M3); no
   * destination route can emit either - destinations carry no short code, and
   * this module posts only to /api/destinations. This map is
   * `Record<RegistryErrorCode, string>`, so adding a code to the shared list
   * without these two entries does not compile. That coupling is the point: one
   * list, and a new code cannot ship with a screen that silently falls back to
   * the generic sentence. The wording is honest rather than blank, in case a
   * future destination route ever does grow a coded conflict.
   */
  short_code_conflict: 'Another record already uses that code. Try different letters.',
  short_code_locked: 'That code has already been used and cannot change now. Ask Alex.',
  /**
   * ⛔ ALSO DEAD HERE, AND FOR A DIFFERENT REASON WORTH STATING (T1719 M4):
   * `account_busy` is raised only on the routes that carry the mapping - the two
   * account writes and the two booking doors - and no destination route does.
   * The entry exists because the `Record` is exhaustive.
   * ⚠️ ITS WORDING NOW MATCHES THE ACCOUNT MAP'S, and that convergence is the
   * point rather than a copy-paste: T1719 M4c made BOTH sentences
   * resource-neutral, because the server predicate keys on the SQLSTATE alone
   * and `lock_timeout` bounds every lock wait on the pool. Naming a resource was
   * measurably false in both maps.
   */
  account_busy:
    'Another change is being saved right now. Nothing was saved. Wait a moment and press save again.',
  /**
   * ⛔ DEAD HERE TOO, AND THE THIRD DISTINCT REASON ON THIS MAP (T1719 M6).
   * These three are `readAccountWrite`'s short-code refusals; destinations carry
   * no short code and this module posts only to /api/destinations, so no
   * destination route can emit any of them.
   * ⚠️ DELIBERATELY SHORTER THAN THE ACCOUNT MAP'S. Those sentences are written
   * for the box Helen is looking at on the Accounts screen and end with "Nothing
   * was saved" because a whole account edit was refused; there is no such box
   * and no such edit here. Writing an honest short sentence beats copying one
   * that describes a screen this module never renders.
   * ⛔ PRESENT BECAUSE THE `Record` IS EXHAUSTIVE - that coupling is what stops a
   * new code shipping with a screen that silently falls back to the generic
   * sentence.
   */
  short_code_shape: 'A code must be two to five capital letters, A to Z.',
  short_code_clinical: 'That code reads as a clinical or ward word. Try different letters.',
  short_code_reserved: 'ECM is kept for the references this system makes itself.',
};

/**
 * Read the refusal code off an untrusted response body (never rendered raw).
 * Returns null when the body carries no code, which is the COMMON case and must
 * stay one: the missing-name and missing-reason refusals send no code, and they
 * have to keep the generic sentence below.
 */
function registryErrorCodeOf(body: unknown): RegistryErrorCode | null {
  if (typeof body !== 'object' || body === null || !('code' in body)) return null;
  const code = (body as { code: unknown }).code;
  return isRegistryErrorCode(code) ? code : null;
}

/**
 * Every registry write carries the operator's reason, and it is required.
 *
 * ⭐ IT IS STORED, NOT DECORATIVE. It lands in the v27 lifecycle journal beside
 * who acted and when, inside the same transaction as the change - so if the
 * trail cannot be written the change does not happen either. S2 was dissolved
 * for these writes at M3 only because there was nowhere to put it.
 */
export type RegistryNote = { note: string };

export type DestinationResult = { ok: true } | { ok: false; message: string };

function destinationFailure(e: unknown, verb: string): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 409) {
    return 'There is already a destination with that name. Check the list, or use a name that tells them apart.';
  }
  if (status === 404) return 'That destination is no longer on the list. Reload and try again.';
  if (status === 400) {
    // ⛔ THE CODE ONLY EVER NARROWS. A 400 that carries a code we recognise gets
    // the specific sentence; anything else — no code, or one this build does not
    // know — falls through to the sentence that was always here. That fallback
    // is load-bearing rather than defensive: the missing-name and missing-reason
    // refusals send no code at all, and they are the commonest 400 on this form.
    const code = e instanceof ApiError ? registryErrorCodeOf(e.body) : null;
    if (code !== null) return REGISTRY_ERROR_COPY[code];
    return 'Check the details. A destination needs a name and a short reason for the change.';
  }
  return `Could not ${verb}. Try again in a moment.`;
}

export async function createDestination(name: string, note: string): Promise<DestinationResult> {
  try {
    await postJson('/api/destinations', { name, note });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: destinationFailure(e, 'add that destination') };
  }
}

/** ⛔ A RENAME, NOT A MERGE. Renaming onto a name already on the list is
 *  refused; the two rows are never folded into one. The id does not move, so
 *  every price and every booking already recorded keeps pointing at this row. */
export async function renameDestination(
  destinationId: number,
  name: string,
  note: string,
): Promise<DestinationResult> {
  try {
    await postJson('/api/destinations/rename', { destinationId, name, note });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: destinationFailure(e, 'save that destination') };
  }
}

/** R38 - deactivate by flag, never delete. Hidden from new work; every price and
 *  every booking already taken keeps it. */
export async function setDestinationActive(
  destinationId: number,
  active: boolean,
  note: string,
): Promise<DestinationResult> {
  try {
    await postJson('/api/destinations/active', { destinationId, active, note });
    return { ok: true };
  } catch (e) {
    return {
      ok: false,
      message: destinationFailure(
        e,
        active ? 'put that destination back in use' : 'take that destination out of use',
      ),
    };
  }
}
